import { AccountListingType, Box, Card, CardLogo, MaterialSymbol, withStyles } from 'vopay';
import { SideDrawer, Title, DetailsRows, UtilButton, Button, NoFillButton } from 'UI';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { usePermissions } from '@/providers/Permissions.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { Account } from './types';
import useDeleteBankAccount from '@/hooks/useApi/useDeleteBankAccount';
import useDeleteCreditCard from '@/hooks/useApi/useDeleteCreditCard';
import styles from './AccountDetailsDrawer.module.scss';

const cn = withStyles(styles);

function formatBankAccountNumber(account: Account | null) {
  if (!account) return '';

  const accountNumber = account.AccountNumber || '';

  if (account.Country === 'CA') {
    return `${account.FinancialInstitutionNumber || ''}-${account.BranchTransitNumber || ''}-${accountNumber}`;
  }
  if (account.Country === 'US') {
    return `${account.ABARoutingNumber || ''}-${accountNumber}`;
  }
  return accountNumber;
}

type AccountDetailsDrawerProps = {
  isOpen: boolean;
  account: Account | null;
  onClose: () => void;
  onSetAsDefault: () => void;
  onDeleted: () => void;
  accountListingType: AccountListingType;
};

export default function AccountDetailsDrawer({
  isOpen,
  account,
  onClose,
  onSetAsDefault,
  onDeleted,
  accountListingType,
}: AccountDetailsDrawerProps) {
  const { permissions } = usePermissions();
  const { response } = useClientData();
  const { setOpenModule } = useOpenModule();
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { deleting: deletingBankAccount, deleteBankAccount } = useDeleteBankAccount();
  const { deleting: deletingCreditCard, deleteCreditCard } = useDeleteCreditCard();
  const deleting = deletingBankAccount || deletingCreditCard;
  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.MyBankAccounts.Details.AccountDetails',
  });
  const { t: tDrawer } = useTranslation('translations', {
    keyPrefix: 'Accounts.MyBankAccounts.Details.BankAccountDetailsContainer',
  });
  const { t: tGlobal } = useTranslation('translations');

  async function handleDeleteAccount() {
    if (!account) return;

    const result = await alertDialog.caution(
      tDrawer('DeleteConfirm.title', { defaultValue: 'Delete Account' }),
      tDrawer('DeleteConfirm.subTitle', {
        defaultValue: 'Are you sure you want to delete this account? This action cannot be undone.',
      }),
      {
        abortLabel: tGlobal('Global.Buttons.cancel'),
        confirmLabel: tDrawer('Button.delete', { defaultValue: 'Delete' }),
      },
    );

    if (result !== 'confirm') return;

    try {
      if (account.Type === 'Credit Card') {
        await deleteCreditCard({
          PortalUser: response.LoginUserName,
          CreditCardToken: account.CreditCardToken || '',
        });
      } else {
        await deleteBankAccount({ PortalUser: response.LoginUserName, Token: account.Token || '' });
      }

      toast.success(tDrawer('DeleteConfirm.success', { defaultValue: 'Account deleted successfully.' }));
      onClose();
      onDeleted();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      await alertDialog.error(
        tDrawer('DeleteConfirm.errorTitle', { defaultValue: 'Unable to Delete Account' }),
        errorMessage,
        { confirmLabel: tGlobal('Global.Buttons.okay') },
      );
    }
  }

  const bankAccountNumber = formatBankAccountNumber(account);

  const headingAccountLabel =
    account?.Type === 'Credit Card' ? account.CreditCardNumber || '-' : formatBankAccountNumber(account) || '-';

  const isDefault = !!account?.IsDefault;

  const isDefaultForAnyPurpose =
    isDefault || !!account?.IsOperationalDefault || !!account?.IsBillingDefault || !!account?.IsDefaultAccount;

  const isButtonsAvailable =
    isDefault && accountListingType === 'Operational' && response.SubAccountType !== 'franchise';

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        accountListingType === 'Billing'
          ? tDrawer('title.billingAccountDetails', { defaultValue: 'Billing Account Details' })
          : tDrawer('title.operationalAccountDetails', { defaultValue: 'Operational Account Details' })
      }
      modal={false}
    >
      {account && (
        <Box flx="col" gapSize="16px">
          <Card className={cn('card')}>
            <Box jc="sb" ai="center" width="100%">
              <Box gapSize="16px" ai="center">
                <div className={cn('headingIcon')}>
                  <MaterialSymbol
                    icon={account.Type === 'Credit Card' ? 'credit_card' : 'account_balance'}
                    size="24px"
                    color="#fff"
                  />
                </div>
                <Title level="3">{headingAccountLabel}</Title>
              </Box>
              {isDefault ? (
                <div className={cn('headingDefaultLabel')}>
                  {t('defaultInformation.default', { defaultValue: 'Default' })}
                </div>
              ) : (
                <UtilButton
                  size="medium"
                  disabled={
                    deleting ||
                    (accountListingType === 'Billing'
                      ? !permissions.ManageAccountandBilling
                      : !permissions.ManageDefaultPayments)
                  }
                  onClick={onSetAsDefault}
                >
                  {tDrawer('Button.setAsDefault', { defaultValue: 'Set as Default' })}
                </UtilButton>
              )}
            </Box>
          </Card>

          {isButtonsAvailable && (
            <Box flx="row" gap="sm" height="auto">
              <Button
                id="operational-account-drawer-add-funds"
                colorScheme="secondary"
                size="large"
                iconLeft="arrow_upward"
                disabled={deleting}
                onClick={() => {
                  setOpenModule('myAccount');
                  history.push({
                    pathname: '/fund-my-account',
                    state: {
                      token: account.Token,
                      type: account.Type,
                      currency: account.Currency,
                      cardToken: account.CreditCardToken,
                      account,
                    },
                  });
                }}
              >
                {tDrawer('Button.addFunds', { defaultValue: 'Add Funds' })}
              </Button>
              {account.Type !== 'Credit Card' && (
                <Button
                  id="operational-account-drawer-withdraw"
                  colorScheme="secondary"
                  size="large"
                  iconLeft="arrow_downward"
                  disabled={deleting}
                  onClick={() => {
                    setOpenModule('myAccount');
                    history.push({
                      pathname: '/withdraw-my-account',
                      state: {
                        token: account.Token,
                        type: account.Type,
                        currency: account.Currency,
                        cardToken: account.CreditCardToken,
                        account,
                      },
                    });
                  }}
                >
                  {tDrawer('Button.withdraw', { defaultValue: 'Withdraw' })}
                </Button>
              )}
            </Box>
          )}

          <Card className={cn('card')}>
            <Title level="4" className={cn('title')}>
              {tDrawer('subTitle', { defaultValue: 'Account Info' })}
            </Title>

            <DetailsRows
              className={cn('rows')}
              details={[
                {
                  label: t('PersonalInformation.accountHolderName', { defaultValue: 'Account Holder Name' }),
                  value: account.AccountHolderName || account.CreditCardHolderName || '-',
                },
                {
                  label: t('PersonalInformation.contactNumber', { defaultValue: 'Contact Number' }),
                  value: account.Phone || '-',
                  visible: account.Type === 'Bank Account',
                },
                {
                  label: t('PersonalInformation.email', { defaultValue: 'Email' }),
                  value: account.EmailAddress || '-',
                  visible: account.Type === 'Bank Account',
                },
              ]}
            />

            <hr className={cn('divider')} />

            <DetailsRows
              className={cn('rows')}
              details={[
                {
                  label: t('BankAccountInformation.institutionName', { defaultValue: 'Institution Name' }),
                  value: account.InstitutionName,
                  visible: account.Type === 'Bank Account',
                },
                {
                  label: t('BankAccountInformation.accountNumber', { defaultValue: 'Account Number' }),
                  value: bankAccountNumber,
                  visible: account.Type === 'Bank Account',
                },
                {
                  label: t('CreditCardClientDetails.accountNumber', { defaultValue: 'Account Number' }),
                  value: (
                    <Box flx="row" ai="center" jc="fe" gap="sm">
                      {account.CreditCardBrand && (
                        <CardLogo width="34px" height="28px" cardBrand={account.CreditCardBrand} />
                      )}
                      <span>{account.CreditCardNumber || '-'}</span>
                    </Box>
                  ),
                  visible: account.Type === 'Credit Card',
                },
                {
                  label: t('CreditCardClientDetails.expirationDate', { defaultValue: 'Expiration Date' }),
                  value: `${account.CreditCardExpiryMonth || ''} / ${account.CreditCardExpiryYear || ''}`,
                  visible: account.Type === 'Credit Card',
                },
                {
                  label: t('CreditCardClientDetails.accountType', { defaultValue: 'Account Type' }),
                  value: tGlobal(`Accounts.MyBankAccounts.accountTypeItem.${account.Type}`, {
                    defaultValue: account.Type,
                  }),
                },
                {
                  label: t('CreditCardClientDetails.currency', { defaultValue: 'Currency' }),
                  value: account.Currency || '-',
                },
              ]}
            />

            <hr className={cn('divider')} />

            <DetailsRows
              className={cn('rows')}
              details={[
                {
                  label: t('BankAccountInformation.country', { defaultValue: 'Country' }),
                  value: account.Country || '-',
                },
                {
                  label: t('AddressInformation.address', { defaultValue: 'Address' }),
                  value: account.Address || '-',
                  visible: !!account.Address,
                },
                {
                  label: t('AddressInformation.city', { defaultValue: 'City' }),
                  value: account.City,
                  visible: !!account.City,
                },
                {
                  label: t('AddressInformation.province', { defaultValue: 'Province/State' }),
                  value: account.Province,
                  visible: !!account.Province,
                },
                {
                  label: t('AddressInformation.postalCode', { defaultValue: 'Postal / ZIP Code' }),
                  value: account.PostalCode || '-',
                  visible: !!account.Province,
                },
              ]}
            />

            <Box jc="sb" ai="center" mt="sm" height="auto">
              {account.DateAdded && (
                <div className={cn('caption')}>{`${t('description', { defaultValue: 'Account added on' })} ${
                  account.DateAdded
                }`}</div>
              )}
            </Box>
          </Card>

          {!isDefaultForAnyPurpose && (
            <Box jc="fe" pb="sm">
              <NoFillButton colorScheme="destructive" size="large" disabled={deleting} onClick={handleDeleteAccount}>
                {deleting
                  ? tDrawer('Button.deletingAccount', { defaultValue: 'Deleting...' })
                  : tDrawer('Button.deleteAccount', { defaultValue: 'Delete Account' })}
              </NoFillButton>
            </Box>
          )}
        </Box>
      )}
    </SideDrawer>
  );
}
